import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiClock, FiStar, FiAward, FiZap, FiTarget, FiTrendingUp } = FiIcons;

function MultiplicationJungleGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  
  // Game Modes: 'menu', 'classic', 'find-parents', 'table-day', 'boss', 'result'
  const [gameMode, setGameMode] = useState('menu');
  const [selectedTable, setSelectedTable] = useState(null); // null=mixed, or 1-10
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(0);
  const [score, setScore] = useState(0);
  const [coins, setCoins] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  
  // Current question
  const [currentFact, setCurrentFact] = useState(null); // {num1, num2, product}
  const [productCards, setProductCards] = useState([]); // Array of products to choose from
  const [selectedCard, setSelectedCard] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct', 'wrong', 'timeout'
  
  // Boss mode progress
  const [bossProgress, setBossProgress] = useState(0); // 0 to 5
  
  // Table of the Day
  const [tableOfDay] = useState(() => Math.floor(Math.random() * 10) + 1);

  // Generate multiplication fact
  const generateFact = (table = null, mode = 'classic') => {
    let num1, num2;
    if (table && table !== 'mixed') {
      // Specific table
      num1 = table;
      num2 = Math.floor(Math.random() * 10) + 1;
    } else {
      // Mixed tables
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
    }
    
    const product = num1 * num2;
    // Determine time based on factors
    const timeLimit = (num1 <= 5 && num2 <= 5) ? 4 : 10;
    
    return { num1, num2, product, timeLimit };
  };

  // Generate product cards (including correct answer and distractors)
  const generateProductCards = (correctProduct, count = 12) => {
    const cards = new Set([correctProduct]);
    while (cards.size < count) {
      const n1 = Math.floor(Math.random() * 10) + 1;
      const n2 = Math.floor(Math.random() * 10) + 1;
      cards.add(n1 * n2);
    }
    return Array.from(cards)
      .map(value => ({ value, isCorrect: value === correctProduct }))
      .sort(() => Math.random() - 0.5);
  };

  // Generate "Find the Parents" cards (show products, find facts)
  const generateParentCards = (correctNum1, correctNum2, count = 12) => {
    const correctFact = `${correctNum1} × ${correctNum2}`;
    const cards = new Set([correctFact]);
    while (cards.size < count) {
      const n1 = Math.floor(Math.random() * 10) + 1;
      const n2 = Math.floor(Math.random() * 10) + 1;
      cards.add(`${n1} × ${n2}`);
    }
    return Array.from(cards)
      .map(value => ({ value, isCorrect: value === correctFact }))
      .sort(() => Math.random() - 0.5);
  };

  // Start a new round
  const startNewRound = () => {
    const fact = generateFact(selectedTable, gameMode);
    setCurrentFact(fact);
    
    if (gameMode === 'find-parents') {
      // Show product, find fact
      setProductCards(generateParentCards(fact.num1, fact.num2));
    } else {
      // Show fact, find product
      setProductCards(generateProductCards(fact.product));
    }
    
    setTimeLeft(fact.timeLimit);
    setMaxTime(fact.timeLimit);
    setSelectedCard(null);
    setFeedback(null);
  };

  // Timer countdown - Robust integer-like approach for display
  useEffect(() => {
    if ((gameMode === 'classic' || gameMode === 'find-parents' || gameMode === 'table-day' || gameMode === 'boss') && timeLeft > 0 && !feedback) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 0.1) {
            handleTimeout();
            return 0;
          }
          return Math.max(0, prev - 0.1);
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [gameMode, timeLeft, feedback]);

  const handleTimeout = () => {
    setFeedback('timeout');
    setStreak(0);
    dispatch({ type: 'ADD_WRONG_ANSWER' });
    
    if (gameMode === 'boss') {
      // Failed boss level
      setTimeout(() => setGameMode('result'), 1500);
    } else {
      setTimeout(() => startNewRound(), 1500);
    }
  };

  const handleCardClick = (card) => {
    if (feedback || selectedCard) return;
    
    setSelectedCard(card.value);
    
    if (card.isCorrect) {
      // Correct answer!
      const basePoints = Math.ceil(timeLeft) * 10;
      const streakBonus = streak * 5;
      const tableDayBonus = (selectedTable === tableOfDay) ? basePoints : 0;
      const totalPoints = basePoints + streakBonus + tableDayBonus;
      
      setScore(prev => prev + totalPoints);
      setCoins(prev => prev + (selectedTable === tableOfDay ? 2 : 1));
      setStreak(prev => prev + 1);
      setBestStreak(prev => Math.max(prev, streak + 1));
      setFeedback('correct');
      dispatch({ type: 'ADD_SCORE', payload: totalPoints });

      // Boss mode progress
      if (gameMode === 'boss') {
        const newProgress = bossProgress + 1;
        setBossProgress(newProgress);
        if (newProgress >= 5) {
          // Boss defeated!
          setTimeout(() => {
            dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'multiplication-jungle', bonusTokens: 50 } });
            setGameMode('result');
          }, 1500);
          return;
        }
      }
      
      setTimeout(() => startNewRound(), 1000);
    } else {
      // Wrong answer
      setFeedback('wrong');
      setStreak(0);
      dispatch({ type: 'ADD_WRONG_ANSWER' });
      
      if (gameMode === 'boss') {
        setTimeout(() => setGameMode('result'), 1500);
      } else {
        setTimeout(() => {
          setFeedback(null);
          setSelectedCard(null);
        }, 1000);
      }
    }
  };

  const startGame = (mode, table = 'mixed') => {
    setGameMode(mode);
    setSelectedTable(table);
    setScore(0);
    setCoins(0);
    setStreak(0);
    setBossProgress(0);
    startNewRound();
  };

  // Menu Screen
  const renderMenu = () => (
    <div className="max-w-6xl mx-auto p-4">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-gradient-to-br from-green-50 to-yellow-50 rounded-3xl p-8 shadow-2xl border-4 border-green-400"
      >
        {/* Title */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🏴‍☠️💎</div>
          <h1 className="text-4xl font-kid text-green-700 mb-2">Multiplication Jungle</h1>
          <p className="text-green-600 font-comic text-lg">
            Race against time to find treasure products!
          </p>
        </div>

        {/* Table of the Day Banner */}
        <motion.div 
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="bg-yellow-100 border-4 border-yellow-400 rounded-2xl p-4 mb-6 text-center"
        >
          <div className="flex items-center justify-center gap-3">
            <SafeIcon icon={FiStar} className="text-yellow-500 text-3xl" />
            <div>
              <div className="font-kid text-yellow-700 text-xl">Table of the Day: {tableOfDay}×</div>
              <div className="font-comic text-yellow-600 text-sm">Earn 2× Coins on this table!</div>
            </div>
            <SafeIcon icon={FiStar} className="text-yellow-500 text-3xl" />
          </div>
        </motion.div>

        {/* Game Modes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Classic Mode */}
          <div className="bg-white rounded-2xl p-6 border-2 border-blue-300 shadow-lg">
            <h2 className="text-2xl font-kid text-blue-600 mb-3 flex items-center justify-center gap-2">
              <SafeIcon icon={FiTarget} /> Classic Hunt
            </h2>
            <p className="font-comic text-gray-600 text-sm mb-4 text-center">
              See <strong>3 × 4</strong>, find <strong>12</strong>!
            </p>
            <div className="mb-4">
              <p className="font-comic text-sm text-gray-500 mb-2 text-center">Choose a table:</p>
              <div className="grid grid-cols-5 gap-2">
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <button
                    key={num}
                    onClick={() => startGame('classic', num)}
                    className={`py-2 rounded-lg font-kid text-lg transition-all ${
                      num === tableOfDay 
                        ? 'bg-yellow-400 text-white hover:bg-yellow-500 shadow-lg' 
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                  >
                    {num}{num === tableOfDay && '⭐'}
                  </button>
                ))}
              </div>
            </div>
            <button 
              onClick={() => startGame('classic', 'mixed')}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-kid py-3 rounded-xl shadow-lg transition-all"
            >
              Mixed Tables Challenge
            </button>
          </div>

          {/* Find the Parents Mode */}
          <div className="bg-white rounded-2xl p-6 border-2 border-purple-300 shadow-lg">
            <h2 className="text-2xl font-kid text-purple-600 mb-3 flex items-center justify-center gap-2">
              <SafeIcon icon={FiZap} /> Find the Parents
            </h2>
            <p className="font-comic text-gray-600 text-sm mb-4 text-center">
              See <strong>12</strong>, find <strong>3 × 4</strong>!
            </p>
            <div className="text-6xl text-center mb-4">🔍</div>
            <button 
              onClick={() => startGame('find-parents', 'mixed')}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white font-kid py-3 rounded-xl shadow-lg transition-all"
            >
              Start Detective Mode
            </button>
          </div>

          {/* Table of the Day Focus */}
          <div className="bg-white rounded-2xl p-6 border-2 border-yellow-300 shadow-lg">
            <h2 className="text-2xl font-kid text-yellow-600 mb-3 flex items-center justify-center gap-2">
              <SafeIcon icon={FiTrendingUp} /> Table of the Day
            </h2>
            <p className="font-comic text-gray-600 text-sm mb-4 text-center">
              Master the <strong>{tableOfDay}× table</strong> today!
            </p>
            <div className="text-6xl text-center mb-4">📅</div>
            <button 
              onClick={() => startGame('table-day', tableOfDay)}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-kid py-3 rounded-xl shadow-lg transition-all"
            >
              Practice {tableOfDay}× Table
            </button>
          </div>

          {/* Boss Level */}
          <div className="bg-gradient-to-br from-red-100 to-orange-100 rounded-2xl p-6 border-2 border-red-300 shadow-lg">
            <h2 className="text-2xl font-kid text-red-600 mb-3 flex items-center justify-center gap-2">
              <SafeIcon icon={FiAward} /> Boss Battle
            </h2>
            <p className="font-comic text-gray-600 text-sm mb-4 text-center">
              Get <strong>5 correct in a row</strong> to defeat the boss!
            </p>
            <div className="text-6xl text-center mb-4">🐉</div>
            <button 
              onClick={() => startGame('boss', 'mixed')}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-kid py-3 rounded-xl shadow-lg transition-all"
            >
              Challenge the Boss!
            </button>
          </div>
        </div>

        {/* Instructions */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-blue-50 rounded-2xl p-6 border-2 border-blue-200"
        >
          <h3 className="text-xl font-kid text-blue-700 mb-4 text-center">How to Play 🎮</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-comic text-gray-700">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⏱️</div>
              <div>
                <strong>Race the Clock:</strong> You have 4 seconds if both numbers are small (1-5), or 10 seconds if any number is bigger (6-10)!
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🎯</div>
              <div>
                <strong>Find the Answer:</strong> Click the correct product card before time runs out!
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🔥</div>
              <div>
                <strong>Build Streaks:</strong> Answer multiple questions correctly in a row for bonus points!
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-2xl">🪙</div>
              <div>
                <strong>Collect Coins:</strong> Every correct answer earns coins! Play the Table of the Day for 2× coins!
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );

  // Game Screen
  const renderGame = () => {
    if (!currentFact) return null;
    
    const displayQuestion = gameMode === 'find-parents' 
      ? currentFact.product 
      : `${currentFact.num1} × ${currentFact.num2}`;
      
    const questionLabel = gameMode === 'find-parents'
      ? 'Find the multiplication fact!'
      : 'Find the product!';

    return (
      <div className="max-w-4xl mx-auto p-4">
        {/* HUD */}
        <div className="flex justify-between items-center mb-6 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border-2 border-green-200">
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2">
              <span className="text-2xl">🪙</span>
              <span className="font-kid text-green-700 text-xl">{coins}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔥</span>
              <span className="font-kid text-orange-600 text-xl">{streak}</span>
            </div>
            {gameMode === 'boss' && (
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiAward} className="text-red-500 text-xl" />
                <span className="font-kid text-red-600 text-xl">{bossProgress}/5</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-2xl text-gray-600" />
            <div className="w-32 h-4 bg-gray-200 rounded-full overflow-hidden border-2 border-gray-300">
              <motion.div 
                className={`h-full ${timeLeft < 2 ? 'bg-red-500' : 'bg-green-500'}`}
                animate={{ width: `${(timeLeft / maxTime) * 100}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </div>
        </div>

        {/* Question */}
        <motion.div 
          key={displayQuestion}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center mb-8"
        >
          <div className="inline-block bg-white rounded-3xl px-12 py-8 shadow-2xl border-4 border-green-400">
            <div className="text-7xl font-kid text-green-700 mb-2">{displayQuestion}</div>
            <div className="text-green-600 font-comic text-lg">{questionLabel}</div>
          </div>
        </motion.div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
          {productCards.map((card, idx) => (
            <motion.button
              key={`${idx}-${card.value}`}
              layout
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleCardClick(card)}
              disabled={!!feedback}
              className={`
                h-24 rounded-2xl text-3xl font-kid shadow-lg border-b-4 transition-all
                ${feedback === 'correct' && card.isCorrect 
                  ? 'bg-green-400 border-green-600 text-white scale-110' 
                  : feedback === 'wrong' && selectedCard === card.value
                  ? 'bg-red-400 border-red-600 text-white'
                  : 'bg-yellow-50 border-yellow-300 text-green-700 hover:bg-yellow-100'}
              `}
            >
              {card.value}
            </motion.button>
          ))}
        </div>

        {/* Feedback Animations */}
        <AnimatePresence>
          {feedback === 'correct' && (
            <motion.div 
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 pointer-events-none flex items-center justify-center z-50"
            >
              <div className="text-9xl">✨</div>
            </motion.div>
          )}
          {feedback === 'wrong' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, x: [-10, 10, -10, 10, 0] }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 pointer-events-none flex items-center justify-center z-50"
            >
              <div className="text-9xl">❌</div>
            </motion.div>
          )}
          {feedback === 'timeout' && (
            <motion.div 
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 pointer-events-none flex items-center justify-center z-50"
            >
              <div className="text-9xl">⏰</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Result Screen
  const renderResult = () => (
    <div className="max-w-md mx-auto p-4">
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-green-400 text-center"
      >
        <div className="text-6xl mb-4">
          {gameMode === 'boss' && bossProgress >= 5 ? '👑' : '🏴‍☠️'}
        </div>
        <h2 className="text-3xl font-kid text-green-700 mb-2">
          {gameMode === 'boss' && bossProgress >= 5 ? 'Boss Defeated!' : 'Round Complete!'}
        </h2>
        <p className="font-comic text-green-600 mb-6">
          {gameMode === 'boss' && bossProgress >= 5 ? "You are the Multiplication Master!" : "Great treasure hunting!"}
        </p>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
            <div className="text-3xl mb-2">🪙</div>
            <div className="font-kid text-2xl text-yellow-700">{coins}</div>
            <div className="font-comic text-sm text-yellow-600">Coins Earned</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
            <div className="text-3xl mb-2">🔥</div>
            <div className="font-kid text-2xl text-orange-700">{bestStreak}</div>
            <div className="font-comic text-sm text-orange-600">Best Streak</div>
          </div>
        </div>
        
        <div className="space-y-3">
          <button 
            onClick={() => setGameMode('menu')}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-kid py-3 rounded-xl shadow-lg transition-all"
          >
            Play Again
          </button>
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-full bg-white hover:bg-gray-50 text-green-700 font-kid py-3 rounded-xl border-2 border-green-200 transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-yellow-300 to-green-500 overflow-hidden">
      {/* Background Decorations */}
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div className="absolute top-10 left-10 text-8xl animate-bounce">🌴</div>
        <div className="absolute bottom-20 right-20 text-8xl animate-bounce" style={{ animationDelay: '1s' }}>💎</div>
        <div className="absolute top-1/3 right-10 text-6xl animate-pulse">🏴‍☠️</div>
        <div className="absolute bottom-1/3 left-20 text-6xl animate-pulse" style={{ animationDelay: '0.5s' }}>⚓</div>
      </div>
      
      <div className="relative z-10 pt-4">
        {gameMode !== 'menu' && (
          <button 
            onClick={() => setGameMode('menu')}
            className="absolute top-4 left-4 bg-white/80 hover:bg-white text-green-700 p-3 rounded-xl shadow-lg transition-all"
          >
            <SafeIcon icon={FiArrowLeft} className="text-2xl" />
          </button>
        )}
        
        {gameMode === 'menu' && renderMenu()}
        {(gameMode === 'classic' || gameMode === 'find-parents' || gameMode === 'table-day' || gameMode === 'boss') && renderGame()}
        {gameMode === 'result' && renderResult()}
      </div>
    </div>
  );
}

export default MultiplicationJungleGame;